import {
  Checkbox,
  RadioGroup,
  Radio,
  FormGroup,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from "react";
import { useId, useState } from "react";
import type {
  ExamForm,
  ExamFormChoiceAnswer,
  ExamFormTestChoice,
} from "@rmh/defs";
import { getSubtype } from "../../utils/examFormUtils";
import AdditionalCommentsInput from "../inputs/AdditionalCommentsInput";
import TestFormContainer from "../inputs/TestFormContainer";
import MultiSelectInput from "../inputs/MultiSelectInput";

interface DisplayMultiChoiceProps {
  item: ExamFormTestChoice;
  examId: string;
  onInputChange?: () => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  statusComponent: ReactNode;
  disabled: boolean;
}

function DisplayMultiChoice({
  item,
  onInputChange,
  setExamState,
  examId,
  statusComponent,
  disabled,
}: DisplayMultiChoiceProps){
  const defaultMultiSelect = (item.examFormChoiceAnswers ?? []).reduce(
    (acc: string[], row) => {
      if (row?.isSelected) return [...acc, row.rowTitle] as string[];
      return acc;
    },
    []
  );
  const defaultSingleSelect = item.examFormChoiceAnswers
    ? item.examFormChoiceAnswers.reduce<string>((acc, cell) => {
        if (cell?.isSelected && acc === "") return String(cell.id);
        return acc;
      }, "")
    : "";
  const [radioValue, setRadioValue] = useState<string>(defaultSingleSelect);
  const [checkboxValue, setCheckboxValue] =
    useState<string[]>(defaultMultiSelect);
  const onRadioChange =
    (index: number) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      setRadioValue(event.target.value);
      if (setExamState) {
        const rowsCopy = [...(item.examFormChoiceRows ?? [])];
        const choiceAnswers = rowsCopy.map((row, i) => ({
          ...row,
          isSelected: i === index,
        }));
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestChoice;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );
          testToUpdate.examFormChoiceAnswers =
            choiceAnswers as ExamFormChoiceAnswer[];
          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };
  const onMultiSelectChange = (newValues: string[]): void => {
    if (setExamState) {
      const rowsCopy = [...(item.examFormChoiceRows ?? [])];
      const choiceAnswers = rowsCopy.map((row) => ({
        ...row,
        isSelected: newValues.includes(row.rowTitle),
      }));
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestChoice;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.examFormChoiceAnswers =
          choiceAnswers as ExamFormChoiceAnswer[];
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };

  const handleAddInputChange = (index: number) => (value: string): void => {
    if (setExamState) {
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestChoice;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        (testToUpdate.examFormChoiceAnswers as ExamFormChoiceAnswer[])[index].text = value;
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };

  const handleCommentChange = (value: string): void => {
    if (setExamState) {
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestChoice;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.commentsText = value;
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };
  const onCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): void => {
    let newValues: string[] = [...checkboxValue];
    if (checked) {
      newValues = [...newValues, event.target.value];
    } else {
      newValues = newValues.filter((nv) => nv !== event.target.value);
    }
    setCheckboxValue(newValues);
    onMultiSelectChange(newValues);
  };
  const testType = getSubtype(item);
  const controlId = useId();

  return (
    <TestFormContainer
      id={controlId}
      statusSlot={statusComponent}
      title={item.testTitle ?? ""}
    >
      {testType === "MultipleChoice" && (
        <RadioGroup
          aria-labelledby={controlId}
          className="flex w-full flex-col"
        >
          {item.examFormChoiceRows?.map((row, index) => (
            <div className="mb-4 flex items-center" key={`radio_${row.id}`}>
              <FormControlLabel
                className="flex flex-1"
                control={
                  <Radio
                    checked={radioValue === row.id}
                    onChange={onRadioChange(index)}
                  />
                }
                disabled={disabled ? radioValue !== row.id : false}
                label={row.rowTitle}
                value={row.id}
              />
              {Boolean(row.addInputText) && (
                <div className="flex flex-1 flex-col">
                  <AdditionalCommentsInput
                    defaultValue={(item.examFormChoiceAnswers as ExamFormChoiceAnswer[])[index].text ?? ""}
                    disabled={disabled}
                    onChange={handleAddInputChange(index)}
                    placeholder="Comments"
                  />
                </div>
              )}
            </div>
          ))}
        </RadioGroup>
      )}
      {testType === "MultipleSelect" && (
        <FormGroup aria-labelledby={controlId} className="flex w-full flex-col">
          {item.examFormChoiceRows?.map((row, index) => (
            <div className="mb-4 flex" key={`radio_${row.id}`}>
              <FormControlLabel
                className="mb-4 flex-1"
                control={
                  <Checkbox
                    checked={checkboxValue.includes(row.rowTitle)}
                    onChange={onCheckboxChange}
                  />
                }
                disabled={
                  disabled ? !checkboxValue.includes(row.rowTitle) : false
                }
                label={row.rowTitle}
                value={row.rowTitle}
              />
              {Boolean(row.addInputText) && (
                <div className="flex-1 flex-col">
                  <AdditionalCommentsInput
                    defaultValue={(item.examFormChoiceAnswers as ExamFormChoiceAnswer[])[index].text ?? ""}
                    disabled={disabled}
                    onChange={handleAddInputChange(index)}
                    placeholder="Comments"
                  />
                </div>
              )}
            </div>
          ))}
        </FormGroup>
      )}
      {testType === "MultipleSelectDropdown" && (
        <MultiSelectInput
          defaultValues={defaultMultiSelect}
          disabled={disabled}
          id={controlId}
          onInputChange={onMultiSelectChange}
        >
          {item.examFormChoiceRows?.map((row) => (
            <MenuItem key={row.rowTitle} value={row.rowTitle}>
              {row.rowTitle}
            </MenuItem>
          ))}
        </MultiSelectInput>
      )}
      {Boolean(item.comments) && (
        <AdditionalCommentsInput
          defaultValue={item.commentsText ?? ""}
          disabled={disabled}
          onChange={handleCommentChange}
        />
      )}
    </TestFormContainer>
  );
}

export default DisplayMultiChoice;
